import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, MapPin, CheckCircle2 } from "lucide-react";
import { ACTIVITIES_PRIMARY, LANGUAGES } from "@/lib/api";

export default function Register() {
  const { t } = useLang();
  const { register } = useAuth();
  const nav = useNavigate();

  const [form, setForm] = useState({
    alias: "",
    password: "",
    city: "",
    languages_spoken: ["English"],
    primary_activities: [],
    secondary_activities: "",
    preferred_group_size: 4,
    latitude: null,
    longitude: null,
  });
  const [loading, setLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  const toggle = (key, val) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
    }));
  };

  const useLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        toast.success(t("locationCaptured"));
        setGeoLoading(false);
      },
      () => {
        toast.error("Could not get location");
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.primary_activities.length === 0) {
      toast.error(t("selectAtLeastOne"));
      return;
    }
    setLoading(true);
    try {
      await register({
        ...form,
        secondary_activities: form.secondary_activities
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      toast.success(t("accountCreated"));
      nav("/discover");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(msg === "Alias already taken" ? t("aliasTaken") : t("networkErr"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-10" data-testid="register-page">
      <div className="w-full max-w-2xl fade-up">
        <div className="bg-white border border-[hsl(var(--border))] rounded-3xl p-6 sm:p-10 shadow-[0_4px_24px_rgba(31,30,27,0.04)]">
          <h1 className="text-3xl font-heading font-semibold tracking-tight">{t("createAccount")}</h1>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{t("tagline")}</p>

          <form onSubmit={submit} className="mt-7 space-y-6">
            {/* Alias + Password */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">{t("alias")}</Label>
                <Input value={form.alias} onChange={(e) => setForm({ ...form, alias: e.target.value })} required className="mt-1.5 rounded-xl h-11" data-testid="reg-alias-input" />
              </div>
              <div>
                <Label className="text-sm">{t("password")}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1.5 rounded-xl h-11" data-testid="reg-password-input" />
              </div>
            </div>

            {/* City + location */}
            <div>
              <Label className="text-sm">{t("city")}</Label>
              <div className="mt-1.5 flex flex-col sm:flex-row gap-2">
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required placeholder="e.g. Dubai" className="rounded-xl h-11 flex-1" data-testid="reg-city-input" />
                <Button
                  type="button"
                  variant="outline"
                  onClick={useLocation}
                  disabled={geoLoading}
                  data-testid="reg-use-location-btn"
                  className="rounded-xl h-11 gap-2 border-[hsl(var(--border))]"
                >
                  {geoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : form.latitude ? <CheckCircle2 className="h-4 w-4 text-[hsl(var(--secondary))]" /> : <MapPin className="h-4 w-4" />}
                  {form.latitude ? t("locationCaptured") : t("useMyLocation")}
                </Button>
              </div>
            </div>

            {/* Languages */}
            <div>
              <Label className="text-sm">{t("languages")}</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {LANGUAGES.map((l) => {
                  const active = form.languages_spoken.includes(l.name);
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => toggle("languages_spoken", l.name)}
                      data-testid={`reg-lang-${l.code}`}
                      className={`px-3.5 py-1.5 rounded-full text-sm border transition-all ${
                        active
                          ? "bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]"
                          : "bg-white text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--secondary))]"
                      }`}
                    >
                      {l.native}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Primary Activities */}
            <div>
              <Label className="text-sm">{t("primaryActivities")}</Label>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {ACTIVITIES_PRIMARY.map((a) => {
                  const active = form.primary_activities.includes(a);
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => toggle("primary_activities", a)}
                      data-testid={`reg-activity-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`px-3 py-2 rounded-2xl text-sm font-medium border transition-all ${
                        active
                          ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))] -translate-y-0.5"
                          : "bg-white text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]"
                      }`}
                    >
                      {a}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Secondary */}
            <div>
              <Label className="text-sm">{t("secondaryActivities")}</Label>
              <Input
                value={form.secondary_activities}
                onChange={(e) => setForm({ ...form, secondary_activities: e.target.value })}
                placeholder={t("secondaryHint")}
                className="mt-1.5 rounded-xl h-11"
                data-testid="reg-secondary-input"
              />
            </div>

            {/* Group size */}
            <div>
              <Label className="text-sm">{t("preferredGroupSize")}: <span className="font-semibold text-[hsl(var(--primary))]">{form.preferred_group_size}</span></Label>
              <div className="mt-3 px-1" data-testid="reg-group-size-slider-wrap">
                <Slider
                  value={[form.preferred_group_size]}
                  min={2}
                  max={10}
                  step={1}
                  onValueChange={(v) => setForm({ ...form, preferred_group_size: v[0] })}
                />
                <div className="mt-1 flex justify-between text-[10px] text-[hsl(var(--muted-foreground))]">
                  <span>2</span><span>10</span>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="reg-submit-btn"
              className="w-full h-11 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white font-medium hover:-translate-y-0.5 transition-all"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("register")}
            </Button>

            <div className="text-center text-sm text-[hsl(var(--muted-foreground))]">
              {t("haveAccount")}{" "}
              <Link to="/login" className="text-[hsl(var(--primary))] font-medium hover:underline" data-testid="go-login-link">
                {t("login")}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
